import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import promiseDone from 'hs-promise-utils/promiseDone';
import { normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { NOT_SPECIFIED, READ_ONLY, HIDDEN, normalizeAccessLevel } from 'customer-data-objects/property/AccessLevel';
import { useHttpClient } from '../client/HttpClientContext';
import { fetchAccessLevel } from './api';
import { getOwnProperty } from '../utils/getOwnProperty';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FETCH_STATUS = {
  UNINITIALIZED: 'UNINITIALIZED',
  PENDING: 'PENDING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED'
};
export const AccessLevelContext = /*#__PURE__*/createContext(null);
const InternalAccessLevelContextProvider = ({
  isDisabled,
  children
}) => {
  const fetchStatusesRef = useRef({});
  const [accessLevelsByObjectTypeId, setAccessLevels] = useReducer((state, {
    objectTypeId,
    accessLevels
  }) => Object.assign({}, state, {
    [objectTypeId]: accessLevels
  }), {});
  const setFetchStatus = useCallback(({
    objectTypeId,
    fetchStatus
  }) => {
    fetchStatusesRef.current = Object.assign({}, fetchStatusesRef.current, {
      [objectTypeId]: fetchStatus
    });
  }, []);

  // Memoized to prevent unnecessary re-renders, see https://reactjs.org/docs/context.html#caveats
  const context = useMemo(() => ({
    isDisabled,
    fetchStatusesRef,
    accessLevelsByObjectTypeId,
    setFetchStatus,
    setAccessLevels
  }), [isDisabled, fetchStatusesRef, accessLevelsByObjectTypeId, setFetchStatus, setAccessLevels]);
  return /*#__PURE__*/_jsx(AccessLevelContext.Provider, {
    value: context,
    children: children
  });
};
export const AccessLevelContextProvider = ({
  children
}) => {
  const parentContext = useContext(AccessLevelContext);
  if (parentContext !== null) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(InternalAccessLevelContextProvider, {
    isDisabled: false,
    children: children
  });
};

/**
 * Any access level hooks called in components in the tree beneath this will
 * return the default NOT_SPECIFIED as if the hooks didn't have a provider
 * (even if there another AccessLevelContextProvider exists beneath this component).
 *
 * Note that this should be used only as an escape hatch and we don't recommend
 * disabling access level checking unless you really need to. Reason being that
 * this only prevents the frontend from knowing if a property is FLP restricted
 * and does NOT override backend FLP enforcement.
 */
export const DisableAccessLevelContext = ({
  children
}) => /*#__PURE__*/_jsx(InternalAccessLevelContextProvider, {
  isDisabled: true,
  children: children
});
const getFetchStatus = (context, objectTypeId) => context.fetchStatusesRef.current[objectTypeId] || FETCH_STATUS.UNINITIALIZED;
const fetchAccessLevelsIfNeeded = ({
  context,
  httpClient,
  objectTypeId
}) => {
  const {
    setFetchStatus,
    setAccessLevels
  } = context;

  // If we don't have the access level permissions in context, fetch them
  const fetchStatus = getFetchStatus(context, objectTypeId);
  if (fetchStatus === FETCH_STATUS.UNINITIALIZED || fetchStatus === FETCH_STATUS.FAILED) {
    setFetchStatus({
      objectTypeId,
      fetchStatus: FETCH_STATUS.PENDING
    });
    promiseDone(fetchAccessLevel(objectTypeId, httpClient).then(accessLevels => {
      setFetchStatus({
        objectTypeId,
        fetchStatus: FETCH_STATUS.SUCCEEDED
      });
      setAccessLevels({
        objectTypeId,
        accessLevels
      });
    }).catch(() => {
      setFetchStatus({
        objectTypeId,
        fetchStatus: FETCH_STATUS.FAILED
      });
    }));
  }
};
export const getAccessLevelWithCaching = ({
  context,
  httpClient,
  objectType,
  propertyName
}) => {
  if (context === null || context.isDisabled) {
    return NOT_SPECIFIED;
  }
  const objectTypeId = normalizeTypeId(objectType);
  if (!objectTypeId || !propertyName) {
    return null;
  }
  fetchAccessLevelsIfNeeded({
    context,
    httpClient,
    objectTypeId
  });
  const fetchStatus = getFetchStatus(context, objectTypeId);
  const accessLevels = context.accessLevelsByObjectTypeId[objectTypeId];
  if (fetchStatus !== FETCH_STATUS.SUCCEEDED || !accessLevels) {
    return null;
  }
  return getOwnProperty(accessLevels, propertyName) || NOT_SPECIFIED;
};
const getAccessLevels = ({
  context,
  httpClient,
  objectType,
  propertyNames = [],
  accessLevelsOverride = {}
}) => propertyNames.reduce((acc, propertyName) => {
  const accessLevelOverride = getOwnProperty(accessLevelsOverride, propertyName);
  const accessLevel = accessLevelOverride !== undefined && accessLevelOverride !== null ? normalizeAccessLevel(accessLevelOverride) : getAccessLevelWithCaching({
    context,
    httpClient,
    objectType,
    propertyName
  });
  acc[propertyName] = accessLevel;
  return acc;
}, {});
export const usePrefetchAccessLevels = ({
  objectTypes
}) => {
  const context = useContext(AccessLevelContext);
  const httpClient = useHttpClient();
  useEffect(() => {
    if (context === null || context.isDisabled) {
      return;
    }
    for (const objectType of objectTypes) {
      const objectTypeId = normalizeTypeId(objectType);
      if (objectTypeId) {
        fetchAccessLevelsIfNeeded({
          context,
          httpClient,
          objectTypeId
        });
      }
    }
  }, [context, httpClient, objectTypes]);
};
export const useAccessLevel = ({
  objectType,
  propertyName,
  accessLevelOverride
}) => {
  const context = useContext(AccessLevelContext);
  const httpClient = useHttpClient();
  if (accessLevelOverride !== undefined && accessLevelOverride !== null) {
    return normalizeAccessLevel(accessLevelOverride);
  }
  return getAccessLevelWithCaching({
    context,
    httpClient,
    objectType,
    propertyName
  });
};
export const useAccessLevels = ({
  objectType,
  propertyNames = [],
  accessLevelsOverride = {}
}) => {
  const context = useContext(AccessLevelContext);
  const httpClient = useHttpClient();
  return useMemo(() => getAccessLevels({
    context,
    httpClient,
    objectType,
    propertyNames,
    accessLevelsOverride
  }), [context, httpClient, objectType, propertyNames, accessLevelsOverride]);
};
const ACCESS_LEVEL_PRIVILEGE_ORDER = [HIDDEN, READ_ONLY, NOT_SPECIFIED];
export const getLowestPrivilegeAccessLevel = accessLevels => Object.values(accessLevels).reduce((least, accessLevel) => {
  if (least === null) {
    return accessLevel;
  }
  if (accessLevel === null) {
    return least;
  }
  if (ACCESS_LEVEL_PRIVILEGE_ORDER.indexOf(accessLevel) < ACCESS_LEVEL_PRIVILEGE_ORDER.indexOf(least)) {
    return accessLevel;
  }
  return least;
}, null);